import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'

export default class extends Controller {
  connect() {
    this.isLoading = false
  }

  get documentId() {
    return (
      this.element.dataset.subscriptionPaymentDateDocumentIdValue ||
      this.element.getAttribute('data-subscription-payment-date-document-id-value')
    )
  }

  get apiUrl() {
    return (
      this.element.dataset.subscriptionPaymentDateApiUrlValue ||
      this.element.getAttribute('data-subscription-payment-date-api-url-value')
    )
  }

  get paymentAmount() {
    return (
      this.element.dataset.subscriptionPaymentDatePaymentAmountValue ||
      this.element.getAttribute('data-subscription-payment-date-payment-amount-value')
    )
  }

  // Handle form submission
  async submitPaymentDateChange(event) {
    event.preventDefault()

    if (this.isLoading) return

    const dateInput = this.element.querySelector(
      '[data-subscription-payment-date-target="dateInput"]'
    )
    const selectedDate = dateInput ? dateInput.value : ''

    if (!selectedDate) {
      this.showError('Please select a date')
      return
    }

    // Validate date (basic client-side check)
    if (!this.isValidDate(selectedDate)) {
      this.showError('Please select a valid future date')
      return
    }

    // Show confirmation dialog
    if (!this.confirmDateChange(selectedDate)) {
      return
    }

    // Immediately dispatch optimistic update event
    this.dispatch('paymentDateUpdated', {
      detail: {
        newDate: selectedDate,
        formattedDate: this.formatDate(selectedDate),
      },
    })

    // Reset form immediately for better UX
    this.resetForm()

    // Handle API call in background
    try {
      this.setLoadingState(true)
      await this.updatePaymentDate(selectedDate)
      this.handleSuccess(selectedDate)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.setLoadingState(false)
    }
  }

  async updatePaymentDate(selectedDate) {
    const apiUrl = this.apiUrl || `/api/subscription_payment_dates/${this.documentId}`

    const response = await patch(apiUrl, {
      body: JSON.stringify({
        payment_date: selectedDate,
      }),
      contentType: 'application/json',
      responseKind: 'json',
    })

    if (!response.ok) {
      const errorData = response.response || {}

      // Handle errors array from API response
      let errorMessage = 'Failed to update payment date. Please try again.'

      if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        errorMessage = errorData.errors.join(', ')
      } else if (errorData.message) {
        errorMessage = errorData.message
      } else if (response.status === 422) {
        errorMessage = 'The selected date is invalid. Please choose a different date.'
      } else if (response.status === 403) {
        errorMessage = 'You do not have permission to change this payment date.'
      } else if (response.status === 404) {
        errorMessage = 'Payment plan not found. Please refresh the page and try again.'
      } else if (response.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.'
      } else if (response.status >= 500) {
        errorMessage = 'Service temporarily unavailable. Please try again later.'
      }

      throw new Error(errorMessage)
    }

    return response.response
  }

  isValidDate(dateString) {
    const selectedDate = new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    selectedDate.setHours(0, 0, 0, 0)

    return selectedDate > today
  }

  confirmDateChange(selectedDate) {
    const selectedDay = new Date(selectedDate + 'T00:00:00').getDate()
    const formattedDate = this.formatDate(selectedDate)
    const paymentAmount = this.paymentAmount || '[amount]'

    const message = `Are you sure you want to change your autopay date to the ${selectedDay} of each month?\n\nYour next payment of ${paymentAmount} will be charged on ${formattedDate}.`

    return window.confirm(message)
  }

  formatDate(dateString) {
    const date = new Date(dateString + 'T00:00:00') // Ensure local timezone
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  setLoadingState(loading) {
    this.isLoading = loading

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      this.submitButtonTarget.textContent = loading ? 'Updating...' : 'Update Payment Date'
    }

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = loading ? 'block' : 'none'
    }
  }

  handleSuccess(selectedDate) {
    this.clearError()

    // Only dispatch success message since optimistic update already happened
    this.dispatch('showSuccess', {
      detail: {
        message: `Payment date successfully updated to ${this.formatDate(selectedDate)}`,
        newDate: selectedDate,
        formattedDate: this.formatDate(selectedDate),
      },
    })
  }

  handleError(error) {
    this.dispatch('showError', {
      detail: {
        message: error.message || 'Failed to update payment date. Please try again.',
      },
    })

    this.showError(error.message || 'Failed to update payment date. Please try again.')
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = 'block'
    } else {
      alert(`Error: ${message}`)
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'none'
      this.errorMessageTarget.textContent = ''
    }
  }

  showSuccessMessage(message) {
    this.dispatch('showSuccess', { detail: { message } })
  }

  resetForm() {
    if (this.hasFormTarget) {
      this.formTarget.reset()
    }

    this.dispatch('formCompleted')
  }

  cancel() {
    this.resetForm()
    this.clearError()
    this.dispatch('formCancelled')
  }

  dispatch(eventName, options = {}) {
    const event = new CustomEvent(`subscription-payment-date:${eventName}`, {
      bubbles: true,
      cancelable: true,
      ...options,
    })
    this.element.dispatchEvent(event)
  }
}
